import { useEffect, useState } from "react"
import { usePage } from "@inertiajs/react"
import PageTitle from "@/components/PageTitle"
import type { Market } from "@/lib/useMarket"
import MarketPicker from "../Sidebar/components/MarketPicker"
import PlaceholderPage from "../Sidebar/components/PlaceholderPage"
import { FALLBACK_MARKETS } from "../Sidebar/sidebarData"
import MenuPanel from "./components/MenuPanel"
import SegmentedTabs from "./components/SegmentedTabs"
import TopBar from "./components/TopBar"
import { GROUPS, type NavPage } from "./topNavData"

type Props = { org_markets?: Market[]; wholesale_market_ids?: string[] }

// Width of the page with the Claude in Chrome panel open on a laptop (rough).
const NARROW_WIDTH = 1000

// Wireframe of navigation without a sidebar: one Menu button opens every
// page at once; pages in the same group switch with tabs in the page title
// row. Market handling is the same as the sidebar wireframe.
export default function TopNavWireframe() {
  const { org_markets, wholesale_market_ids } = usePage<Props>().props
  const markets = org_markets?.length ? org_markets : FALLBACK_MARKETS

  const [pageKey, setPageKey] = useState("home")
  const [menuOpen, setMenuOpen] = useState(false)
  const [market, setMarket] = useState<Market | null>(null)
  const [allMarkets, setAllMarkets] = useState(false)
  const [narrow, setNarrow] = useState(true)

  useEffect(() => {
    if (!menuOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuOpen(false) }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [menuOpen])

  // Wholesale Forecast only for a market with a wholesale channel, or before one is picked.
  const showWholesale = !market || (wholesale_market_ids ?? []).includes(market.id)
  const groups = GROUPS.map((g) => ({ ...g, pages: g.pages.filter((p) => showWholesale || !p.wholesaleOnly) }))
  const group = groups.find((g) => g.pages.some((p) => p.key === pageKey)) ?? groups[0]
  const page = group.pages.find((p) => p.key === pageKey) ?? group.pages[0]

  const open = (p: NavPage) => { setPageKey(p.key); setMenuOpen(false) }
  const changeMarket = (m: Market | null) => {
    setAllMarkets(m === null)
    if (m) setMarket(m)
  }

  const needsPick = page.scope === "market" && !market
  const shownMarket = page.scope === "either" && allMarkets ? null : market
  const groupTabs = !group.noTabs && group.pages.length > 1

  return (
    <div className="flex h-screen flex-col">
      <div className="flex min-h-0 flex-1 bg-muted">
        <div className="relative flex min-w-0 flex-col bg-page-background" style={{ width: narrow ? NARROW_WIDTH : "100%" }}>
          <TopBar
            page={page}
            menuOpen={menuOpen}
            onMenuToggle={() => setMenuOpen((o) => !o)}
            markets={markets}
            market={shownMarket}
            onMarketChange={changeMarket}
          />
          {menuOpen && <MenuPanel groups={groups} active={page.key} onSelect={open} onClose={() => setMenuOpen(false)} />}

          <div className="flex flex-shrink-0 flex-wrap items-center gap-x-5 gap-y-2 px-6 pb-3 pt-4">
            <PageTitle title={page.label} subtitle={page.blurb} />
            {groupTabs && (
              <SegmentedTabs
                label={group.heading}
                tabs={group.pages}
                active={page.key}
                onSelect={(key) => open(group.pages.find((p) => p.key === key)!)}
              />
            )}
          </div>
          <div className="flex flex-1 flex-col gap-4 overflow-auto px-6 pb-5">
            {needsPick ? (
              <MarketPicker pageLabel={page.label} markets={markets} onPick={changeMarket} />
            ) : (
              <PlaceholderPage />
            )}
          </div>
        </div>
        {narrow && (
          <div className="flex flex-1 items-center justify-center border-l border-border text-sm text-muted-foreground">
            Claude panel
          </div>
        )}
      </div>

      <div className="flex flex-shrink-0 flex-wrap items-center gap-x-4 gap-y-1 border-t border-border bg-background px-4 py-1.5 text-xs text-foreground">
        <span className="font-medium">Wireframe</span>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={narrow} onChange={(e) => setNarrow(e.target.checked)} />
          Claude panel open ({NARROW_WIDTH}px page)
        </label>
        <span>Remembered market: {market ? market.name : "none yet"}</span>
      </div>
    </div>
  )
}
